/**
 * The brand assets, and the surface matrix encoded so nobody has to remember it — requirement 6.11.
 *
 * ## The problem the matrix exists to solve
 *
 * All four originals are print rasters, 8-bit RGBA PNG at up to 900 DPI, and **the mark is navy in
 * every one of them.** At `#013368` the mark reaches only 1.67:1 against pure black, so no choice of
 * dark background rescues it — this is a legibility problem rather than a compliance one, since WCAG
 * exempts logotypes from contrast requirements, but a half-vanished mark reads as broken.
 *
 * So a dark surface cannot carry a lockup at all. It gets the **wordmark alone**, cropped away from
 * the mark. The light surfaces get the full lockup. There is no judgement call left at the call site:
 * `<Logo>` reads the active theme and picks.
 *
 * ## The originals, untouched
 *
 * | File | Dimensions | What it is |
 * | --- | --- | --- |
 * | `APR_Hz Full Color Blue.png` | 4543 × 1231 | Horizontal lockup, navy wordmark |
 * | `APR_Vt Full Color Blue.png` | 1468 × 1448 | Vertical lockup, navy wordmark |
 * | `Apuesta-Horizontal-White.png` | 4543 × 1231 | Horizontal, wordmark only reversed to white |
 * | `Logo-White.png` | 1468 × 1448 | Vertical, wordmark only reversed to white |
 *
 * The two "White" files reverse only part of the wordmark: `PUESTA` becomes white while `A` and `PR`
 * stay red. They stay in `/logos` exactly as delivered, as the source of record. Everything below is
 * generated from them into `packages/ui/brand/`.
 *
 * ## Deviation from the design's surface matrix
 *
 * The design's last row reads "Vertical / square contexts: `Logo-White.png` on light,
 * `APR_Vt Full Color Blue.png` on light". The first half cannot be right: a wordmark reversed to white
 * is invisible on a light background, and the file's mark is navy, so it is unusable on a dark one
 * too. Cropping it produces the same wide, short wordmark as cropping its horizontal sibling, so
 * there is no distinct vertical dark asset to make. Vertical-plus-dark therefore falls back to the
 * horizontal wordmark, and closing the gap properly needs the knockout mark already on the design's
 * "still needed" list.
 */
/**
 * Aspect ratios are the *measured* ratios of the generated files — each original cropped to its region
 * and then tight to its own opaque pixels — so `<img>` reserves the right box before the file arrives.
 * `brand/pipeline.cli.ts` recomputes them and throws if they have moved, which is how the wordmark
 * crop's real shape was found: it is 3080 × 626, not 3080 × 1231, because the wordmark occupies only
 * the middle 626 rows of the file it is cut from.
 */
export const logoVariants = {
    'wordmark-white': {
        name: 'apr-wordmark-white',
        widths: [200, 400, 800],
        aspectRatio: 3080 / 626,
        wordmarkOnly: true,
        alt: 'ApuestaPR',
    },
    'lockup-horizontal': {
        name: 'apr-lockup-horizontal',
        widths: [200, 400, 800],
        aspectRatio: 4543 / 1231,
        wordmarkOnly: false,
        alt: 'ApuestaPR',
    },
    'lockup-vertical': {
        name: 'apr-lockup-vertical',
        widths: [160, 320, 640],
        aspectRatio: 1468 / 1448,
        wordmarkOnly: false,
        alt: 'ApuestaPR',
    },
};
/**
 * The surface matrix itself. A dark surface never reaches a variant whose `wordmarkOnly` is false,
 * which `Logo.test.tsx` asserts over the whole table rather than for one case — so a fourth variant
 * added later cannot quietly put a navy mark on navy.
 */
export const surfaceMatrix = {
    dark: {
        horizontal: 'wordmark-white',
        // See the deviation note above: there is no vertical dark asset to fall back to.
        vertical: 'wordmark-white',
    },
    light: {
        horizontal: 'lockup-horizontal',
        vertical: 'lockup-vertical',
    },
};
/** URL prefix `staticAssets` mounts the generated images at. */
export const brandUrlPrefix = '/brand';
/** Formats emitted per width, most preferred first. PNG is the `<img>` fallback. */
export const imageFormats = ['avif', 'webp', 'png'];
export const assetUrl = (variant, width, format) => `${brandUrlPrefix}/${variant.name}-${String(width)}.${format}`;
/** `srcSet` for one format, in the `<width>w` form so the browser can pick against `sizes`. */
export const assetSrcSet = (variant, format) => variant.widths.map((width) => `${assetUrl(variant, width, format)} ${String(width)}w`).join(', ');
//# sourceMappingURL=assets.js.map